import {captureException} from '@sentry/gatsby';
import {getApp, getApps, initializeApp} from 'firebase/app';
import {getFunctions, httpsCallable} from 'firebase/functions';
import {getAuth, signInWithCustomToken} from 'firebase/auth';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

import {MixpanelParams} from './events';
import {getUtmSynapse} from './utmSynapse';

type StripeCheckoutSession = {
  coupon?: string;
  shippingCountry: string;
  storeCountry: string;
  customerEmail?: string;
  lineItems: {
    price: string;
    quantity: number;
  }[];
  successUrl: string;
  cancelUrl: string;
};

export const FIREBASE_CONFIG = {
  projectId: 'the-orca',
  apiKey: 'AIzaSyDKyzvkkb_tECPCAAatqaKtPcmmAugYTFs',
  authDomain: 'the-orca.firebaseapp.com',
};

export type ProductPurchaseStatus = {
  sku: string;
  displayName: string;
  productName: string;
  dateOfPurchase: number;
  isSubscriptionInclusive: boolean;
};

export enum RevenuecatStore {
  'APP_STORE' = 'APP_STORE',
  'PLAY_STORE' = 'PLAY_STORE',
}

export enum ExtensionMethod {
  EXTEND_EXISTING = 'EXTEND_EXISTING',
  NEW_PAYMENT = 'NEW_PAYMENT',
}

export type ActivateOfferingForHubActivationPayload = {
  subscriptionExtensionMethod: ExtensionMethod;
};

export type ExtensionPaymentIntent = {
  clientSecret: string;
  ephemeralKey: string;
  customerId: string;
  publishableKey: string;
  havePaymentMethods: boolean;
};

export type GetOfferingResponse = {
  subscriptionExtensionMethod: ExtensionMethod;
  setupIntent: ExtensionPaymentIntent | undefined;
  iapStoreCancellationRequirement: RevenuecatStore[] | undefined;
};

export type CancelGooglePlaySubscriptionPayload = {
  status: string;
};

export type CanGetOfferingResponse = {
  subscriptionExtensionMethod: ExtensionMethod;
  iapStoreCancellationRequirement: RevenuecatStore[] | undefined;
  isEligibleForExtension: boolean;
  extensionExpiryMiliseconds: number;
};

export type GetAccountOfferingsCheckoutSessionResponse = {
  checkoutSession: {
    url: string;
  };
};

export type ActivateHubsForAccountOfferingResponse = {
  status: string;
};

export type GetDiscourseSSOurlInput = {
  sso: string;
  sig: string;
};

export type IntercomHelpCenterSearchResult = {
  id: string;
  title: string;
  url: string;
};

export type SearchIntercomHelpCenterResponse = {
  results: IntercomHelpCenterSearchResult[];
};

export type IntercomWebIdentityResponse = {
  intercomUserJwt: string;
};

export enum SubscriptionState {
  'NONE' = 'NONE',
  'PENDING_ACTIVATION' = 'PENDING_ACTIVATION',
  'ACTIVE' = 'ACTIVE',
  'OVERDUE_RENEWAL' = 'OVERDUE_RENEWAL',
  'BILLING_ISSUE' = 'BILLING_ISSUE',
  'EXPIRED' = 'EXPIRED',
  'CANCELLED' = 'CANCELLED',
}

export enum SubscriptionProvider {
  MOCKED = 'MOCKED',
  REFERRAL = 'REFERRAL',
  INAPP = 'INAPP',
  STRIPE = 'STRIPE',
}

export type SubscriptionStatus = {
  currentPeriodStart: null | number;
  currentPeriodEnd: null | number;
  cancelAtPeriodEnd: boolean;
  isSubscriptionValid: boolean;
  subscriptionPlanId: string;
  subscriptionProvider: SubscriptionProvider;
};

export type AccountPrivilegesResponse = {
  email: string;
  dateCreated: number;
  isCustomer: boolean;
  isFullRefund: boolean;
  isTrailCustomer: boolean;
  dateFirstLogin: number | null;
  dateSubscriptionRenewed: number | null;
  uid: string | null;
  phoneNumber: string | null;
  subscriptionState: SubscriptionState;
  country: string | null;
  hasBetaAccess: boolean;
  subscriptionStatus?: SubscriptionStatus;
  hardwarePurchaseStatus?: {
    products: ProductPurchaseStatus[];
  };
};

export enum UserProductReviewStatus {
  APPROVED = 'approved',
  PENDING = 'pending',
  REJECTED = 'rejected',
}

export enum ProductUsed {
  ORCA_CORE = 'Orca Core',
  ORCA_DISPLAY = 'Orca Display',
  ORCA_APP = 'Orca App',
}

export enum BoatType {
  SAILBOAT = 'Sailboat',
  MOTORBOAT = 'Motorboat',
}

export enum BoatLength {
  UNDER_25_FEET = 'Under 25 feet (7.5 meters)',
  FEET_25_35 = '25-35 feet (7.5-10.5 meters)',
  FEET_35_45 = '35-45 feet (10.5-13.5 meters)',
  FEET_45_55 = '45-55 feet (13.5-16.5 meters)',
  OVER_55_FEET = 'Over 55 feet (16.5 meters)',
}

export enum Region {
  AFRICA = 'Africa',
  AUSTRALIA = 'Australia',
  BALTIC_SEA = 'Baltic Sea',
  CANADA = 'Canada',
  CARIBBEAN = 'Caribbean',
  DENMARK = 'Denmark',
  FINLAND = 'Finland',
  FRANCE = 'France',
  GERMANY = 'Germany',
  GREECE = 'Greece',
  IRELAND = 'Ireland',
  ITALY = 'Italy',
  MEDITERRANEAN = 'Mediterranean',
  MIDDLE_EAST = 'Middle East',
  NETHERLANDS = 'Netherlands',
  NEW_ZEALAND = 'New Zealand',
  NORWAY = 'Norway',
  SPAIN = 'Spain',
  SWEDEN = 'Sweden',
  UNITED_KINGDOM = 'United Kingdom',
  UNITED_STATES = 'United States',
  SOUTH_PACIFIC_ISLANDS = 'South Pacific Islands',
}

export enum Usage {
  SHORT_CRUISING_TRIPS = 'Short cruising trips',
  RACING = 'Racing',
  LONG_TRIPS = 'Long trips',
  LIVING_ABOARD = 'Living aboard',
  FISHING = 'Fishing',
  OTHER = 'Other',
}

export enum Equipment {
  WIND = 'Wind',
  DEPTH = 'Depth',
  TEMPERATURE = 'Temperature',
  BATTERY_LEVELS = 'Battery levels',
  TANK_LEVELS = 'Tank levels',
  SPEED_THROUGH_WATER = 'Speed through water',
  GNSS_GPS = 'GNSS/GPS',
  COMPASS = 'Compass',
  PRESSURE = 'Pressure',
  AIS_TRANSCEIVER = 'AIS transceiver',
  AUTOPILOT = 'Autopilot',
  RADAR = 'Radar',
  INTERNET = 'Internet',
}

export type PostProductReviewPayload = {
  reviewTitle: string;
  reviewText: string;
  overallRating: number;
  attributeRatings: {
    easeOfUse: number;
    reliability: number;
    valueForMoney: number;
    chartQuality: number;
    routingQuality: number;
  };
  productsUsed: ProductUsed[];
  boat: {
    type: BoatType;
    name: string;
    length: BoatLength;
  };
  regions: Region[];
  usage: Usage[];
  equipment: Equipment[];
  imageData?: string | null;
  imageContentType?: string | null;
};

export type UserProductReviewResponse = {
  uid: string;
  submittedAt: number;
  reviewTitle: string;
  reviewText: string;
  overallRating: number;
  attributeRatings: {
    easeOfUse: number;
    reliability: number;
    valueForMoney: number;
    chartQuality: number;
    routingQuality: number;
  };
  productsUsed: ProductUsed[];
  boat: {
    type: BoatType;
    name: string;
    length: BoatLength;
  };
  regions: Region[];
  usage: Usage[];
  equipment: Equipment[];
  imageUrl: string | null;
  status: UserProductReviewStatus;
  source: 'csv' | 'api';
};

type DeliveryEstimate = {
  minimum?: {
    unit: string;
    value: number;
  };
  maximum?: {
    unit: string;
    value: number;
  };
};

export type StripeHardwareProducts = string;

export type GetShippingEstimatesRequest = {
  currency: string;
  stripeProductIds?: StripeHardwareProducts[];
};

export type ProductShippingEstimates = Partial<
  Record<StripeHardwareProducts, DeliveryEstimate>
>;

export type GetShippingEstimatesResponse = {
  currency: string;
  countries: Record<string, ProductShippingEstimates>;
  range: DeliveryEstimate;
};

export const ensureFirebaseInitialized = () => {
  if (!getApps().length) {
    initializeApp(FIREBASE_CONFIG);
  }
  if (!firebase.apps.length) {
    firebase.initializeApp(FIREBASE_CONFIG);
  }

  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    (window as any).firebase = firebase;
  }
};

ensureFirebaseInitialized();

const app = getApp();
const functions = getFunctions(app, 'europe-west1'); // use us-central1 for emulators
// connectFunctionsEmulator(functions, 'localhost', 5001);
const auth = getAuth(app);

export type McpConnectedApp = {
  id: string;
  clientId: string;
  clientName: string;
  scopes: string[];
  createdAt: number;
  lastUsedAt: number;
  expiresAt: number;
};

type McpApiError = {
  error_description?: string;
};

const MCP_BASE_URL =
  process.env.GATSBY_MCP_BASE_URL || 'https://mcp.getorca.com';

const getMcpAuthorizationHeader = async () => {
  const {currentUser} = firebase.auth();
  if (!currentUser) throw new Error('Sign in to manage integrations.');
  return `Bearer ${await currentUser.getIdToken()}`;
};

export const getMcpConnectedApps = async (): Promise<McpConnectedApp[]> => {
  const response = await fetch(`${MCP_BASE_URL}/oauth/account/grants`, {
    headers: {Authorization: await getMcpAuthorizationHeader()},
  });
  const result = (await response.json()) as
    | {grants: McpConnectedApp[]}
    | McpApiError;
  if (!response.ok || !('grants' in result)) {
    throw new Error(
      ('error_description' in result && result.error_description) ||
        'Integrations could not be loaded.'
    );
  }
  return result.grants;
};

export const revokeMcpConnectedApp = async (grantId: string) => {
  const response = await fetch(`${MCP_BASE_URL}/oauth/account/grants/revoke`, {
    method: 'POST',
    headers: {
      Authorization: await getMcpAuthorizationHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({grantId}),
  });
  if (!response.ok) {
    const result = (await response.json()) as McpApiError;
    throw new Error(
      result.error_description || 'The connected app could not be disconnected.'
    );
  }
};

export const postFacebookEvent = async (
  type: string,
  params: any,
  eventId: string,
  fbc?: string,
  fbp?: string
) => {
  try {
    await httpsCallable(
      functions,
      'postAnalyticsEvent'
    )({
      type,
      params,
      eventId,
      fbc,
      fbp,
    });
  } catch (err) {
    captureException(err);
  }
};

export const getStripeFirebaseDetails = async (id: string) =>
  httpsCallable(
    functions,
    'getStripeOrderDetails'
  )({
    id,
  });

export const getUserIdForEmailOrPhone = async (
  email: string,
  phoneNumber?: string
) =>
  httpsCallable<unknown, {id: string}>(
    functions,
    'getIdForEmailOrPhone'
  )({email, phoneNumber});

export const postMixpanelEvent = async (
  eventName: string,
  deviceId: string,
  uid?: string | null,
  params: Partial<MixpanelParams> = {},
  userIp?: string
) => {
  const utmParams = getUtmSynapse()?.load() ?? {};
  const referral = sessionStorage.getItem('referral_via');

  httpsCallable(
    functions,
    'postMixpanelEvent'
  )({
    eventName,
    deviceId,
    uid,
    params: {
      ...utmParams,
      ...params,
      ...(referral ? {via_referral: referral} : {}),
    },
    userIp,
  });
};

export const getCheckoutSession = async (
  options: StripeCheckoutSession
): Promise<string> => {
  const referral = window?.Rewardful?.referral;

  const res = (await httpsCallable(
    functions,
    'createCheckoutSession'
  )({
    ...options,
    clientReferenceId: referral,
  })) as any;

  return res.data as string;
};

export const validateCheckoutCoupon = async (
  couponId: string,
  priceIds: string[]
) => {
  const res = await httpsCallable<
    {couponId: string; priceIds: string[]},
    {valid: boolean}
  >(
    functions,
    'validateCheckoutCoupon'
  )({couponId, priceIds});
  return res.data.valid;
};

export const getShippingEstimates = async (
  payload: GetShippingEstimatesRequest
) => {
  const res = await httpsCallable<
    GetShippingEstimatesRequest,
    GetShippingEstimatesResponse
  >(
    functions,
    'getShippingEstimates'
  )(payload);

  return res.data;
};

type EmailResponseData = {
  status: 'ok' | 'error';
  message: string;
};

export const addEmailToDisplayTwoWaitingList = async (email: string) => {
  const res = await httpsCallable(
    functions,
    'addEmailToDisplayTwoWaitingList'
  )({
    email,
  });

  return res.data as EmailResponseData;
};

export const getAccountPrivileges = async () => {
  const res = await httpsCallable<null, {data: AccountPrivilegesResponse}>(
    functions,
    'getAccountPrivileges'
  )();

  return res.data.data;
};

export const getHasLinkedRaymarineAxiom = async () => {
  const res = await httpsCallable<null, unknown>(functions, 'getAxioms')();
  return Array.isArray(res.data) && res.data.length > 0;
};

export const getProductReview = async () => {
  const res = await httpsCallable(functions, 'getProductReview')();
  return res.data as UserProductReviewResponse | null;
};

export const postProductReview = async (payload: PostProductReviewPayload) => {
  const res = await httpsCallable(functions, 'postProductReview')(payload);
  return res.data as UserProductReviewResponse;
};

export const deleteProductReview = async () => {
  const res = await httpsCallable(functions, 'deleteProductReview')();
  return res.data as {status: 'ok'};
};

export type PortalSessionResponse = {
  url: string;
};

export type PricingTableResponse = string;

export const getPortalSessionUrl = async () => {
  const res = await httpsCallable<null, PortalSessionResponse>(
    functions,
    'getStripePortalSessionUrl'
  )();

  return res.data;
};

export const getPricingTableId = async () => {
  const res = await httpsCallable<null, PricingTableResponse>(
    functions,
    'getPricingTable'
  )();

  return res.data;
};

export const callSigninWithCustomToken = async (token: string) => {
  try {
    const result = await signInWithCustomToken(auth, token);
    return result;
  } catch (err) {
    return err;
  }
};

export type LinkAccountPayload = {
  stripeCustomerEmail: string;
  stripeReceiptId: string;
};

export type TransferPurchasePayload = {
  orderEmail: string;
  receiptCode: string;
};

export type TransferPurchaseResponse = {
  data: AccountPrivilegesResponse;
  alreadyTransferred: boolean;
};

export const callLinkAccount = async (payload: LinkAccountPayload) => {
  const res = await httpsCallable<
    LinkAccountPayload,
    {data: AccountPrivilegesResponse}
  >(
    functions,
    'linkAccountToStripePurchase'
  )(payload);

  return res.data;
};

export const transferPurchase = async (payload: TransferPurchasePayload) => {
  const res = await httpsCallable<
    TransferPurchasePayload,
    TransferPurchaseResponse
  >(
    functions,
    'transferPurchase'
  )(payload);

  return res.data;
};

export const callGetRevenuecatSubscription = async () => {
  const res = await httpsCallable(functions, 'getRevenuecatSubscription')();

  return res.data as SubscriptionStatus;
};

type FoundUserId = {uid: string};
type UnknownUserId = {status: string};

type UserIdResponse = FoundUserId & UnknownUserId;

export const callGetUserIdForUserHash = async (hash: string) => {
  const res = await httpsCallable(functions, 'getIdentityForHash')({hash});

  return res.data as UserIdResponse;
};

export const activateAccountOffering = async (
  subscriptionExtensionMethod: ExtensionMethod
) => {
  const res = await httpsCallable(
    functions,
    'activateAccountOffering'
  )({
    subscriptionExtensionMethod,
  });

  return res.data as AccountPrivilegesResponse;
};

export const getAccountOffering = async () => {
  const res = await httpsCallable(functions, 'getAccountOffering')({});

  return res.data as GetOfferingResponse;
};

export const canGetAccountOffering = async () => {
  const res = await httpsCallable(
    functions,
    'getCanActivateAccountOffering'
  )({});

  return res.data as CanGetOfferingResponse;
};

export const getAccountOfferingCheckoutSession = async () => {
  const res = await httpsCallable(
    functions,
    'getAccountOfferingCheckoutSession'
  )({});

  return res.data as GetAccountOfferingsCheckoutSessionResponse;
};

export const activateHubsForAccountOffering = async () => {
  const res = await httpsCallable(
    functions,
    'activateHubsForAccountOffering'
  )({});

  return res.data as ActivateHubsForAccountOfferingResponse;
};

export const updateEmailForSupportedServices = async (
  newEmail: string,
  oldEmail: string
) => {
  await httpsCallable(
    functions,
    'updateUserEmail'
  )({
    oldEmail,
    newEmail,
    skipFirebaseAuth: true,
  });
};

export const getDiscourseSSOurl = async (input: GetDiscourseSSOurlInput) => {
  const res = await httpsCallable(functions, 'getDiscourseSSOurl')(input);
  return res.data as string;
};

export const searchIntercomHelpCenter = async (query: string) => {
  const res = await httpsCallable<
    {query: string},
    SearchIntercomHelpCenterResponse
  >(
    functions,
    'searchIntercomHelpCenter'
  )({query});

  return res.data;
};

export const getIntercomWebIdentity = async () => {
  const res = await httpsCallable<null, IntercomWebIdentityResponse>(
    functions,
    'getIntercomWebIdentity'
  )();

  return res.data;
};

export async function updateEmail(newEmail: string, oldEmail: string) {
  await updateEmailForSupportedServices(newEmail, oldEmail);

  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Not authenticated');

  await user.updateEmail(newEmail);
}

export async function updateDisplayName(firstName: string, lastName: string) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Not authenticated');
  const displayName = `${firstName},${lastName}`;
  return user.updateProfile({displayName});
}

export async function removePhoneNumberLink() {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Not authenticated');

  const alreadyLinked = user.providerData.some(
    (p) => p?.providerId === firebase.auth.PhoneAuthProvider.PROVIDER_ID
  );

  if (alreadyLinked) {
    await user.unlink(firebase.auth.PhoneAuthProvider.PROVIDER_ID);
  }
}

/**
 * Renders (or re-uses) an invisible reCAPTCHA and sends an SMS to link the phone.
 * @returns Firebase ConfirmationResult
 */
export async function startPhoneNumberLink(
  fullPhone: string,
  recaptchaVerifier: firebase.auth.RecaptchaVerifier
) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Not authenticated');

  await removePhoneNumberLink();
  return user.linkWithPhoneNumber(fullPhone, recaptchaVerifier);
}

/**
 * Confirm the SMS code the user received.
 * @param confirmationResult from startPhoneNumberLink
 * @param code the 6-digit SMS code
 */
export async function confirmPhoneNumberLink(
  confirmationResult: firebase.auth.ConfirmationResult,
  code: string
) {
  return confirmationResult.confirm(code);
}
